import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReviewForm() {
  const { businessId } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_mobile: "",
    rating: 0
  });

  useEffect(() => {
    fetchBusiness();
  }, [businessId]);

  const fetchBusiness = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/businesses/${businessId}`);
      setBusiness(response.data);
    } catch (error) {
      toast.error("Business not found");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/reviews`, {
        business_id: businessId,
        ...formData
      });

      if (response.data.redirect) {
        // Rating 4 or 5 - redirect to Google
        toast.success("Thank you! Redirecting to Google Reviews...");
        setTimeout(() => {
          window.location.href = response.data.google_review_link;
        }, 1500);
      } else {
        // Rating 3 or below - show thank you message
        toast.success(response.data.message);
        setSubmitted(true);
      }
    } catch (error) {
      toast.error("Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-semibold mb-2">Thank You!</h2>
            <p className="text-muted-foreground">Your feedback has been submitted successfully.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="review-form">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">{business.name}</CardTitle>
          <CardDescription>We'd love to hear your feedback!</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Your Name</Label>
              <Input
                id="name"
                data-testid="customer-name-input"
                placeholder="John Doe"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile Number</Label>
              <Input
                id="mobile"
                data-testid="customer-mobile-input"
                type="tel"
                placeholder="+1234567890"
                value={formData.customer_mobile}
                onChange={(e) => setFormData({ ...formData, customer_mobile: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Rating</Label>
              <div className="flex gap-2 justify-center py-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    data-testid={`rating-star-${star}`}
                    onClick={() => setFormData({ ...formData, rating: star })}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        star <= formData.rating
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
              {formData.rating > 0 && (
                <p className="text-center text-sm text-muted-foreground">
                  You selected {formData.rating} star{formData.rating > 1 ? "s" : ""}
                </p>
              )}
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="submit-review-button"
            >
              {loading ? "Submitting..." : "Submit Review"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}